import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api, { mediaUrl, SITE_URL } from '../api/client';
import Seo from '../components/Seo';
import Breadcrumb from '../components/common/Breadcrumb';
import { PageLoader } from '../components/common/Spinner';
import { BlogCard } from '../components/home/BlogPosts';
import { useSettings } from '../context/SettingsContext';
import { formatDate, stripHtml } from '../utils/format';
import NotFound from './NotFound';

export default function BlogPost() {
  const { slug } = useParams();
  const { settings } = useSettings();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const blogSlug = settings.pages?.blogSlug || 'news';

  useEffect(() => {
    setLoading(true);
    setMissing(false);
    api.get(`/blogs/slug/${slug}`).then(setData).catch(() => setMissing(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <PageLoader />;
  if (missing || !data) return <NotFound />;
  const { post, prev, next, related } = data;
  const base = (settings.seo?.siteUrl || SITE_URL || '').replace(/\/$/, '');
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    image: post.image ? [`${base}${mediaUrl(post.image)}`] : undefined,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt,
    author: { '@type': 'Organization', name: post.author || settings.general?.siteName },
    publisher: { '@type': 'Organization', name: settings.general?.siteName, logo: { '@type': 'ImageObject', url: `${base}${mediaUrl(settings.general?.logo)}` } },
    description: post.excerpt || stripHtml(post.content).slice(0, 200),
    mainEntityOfPage: `${base}/blogs/${blogSlug}/${post.slug}`,
    articleSection: post.category,
    keywords: (post.tags || []).join(', '),
  };
  const title = settings.pages?.blogTitle || 'News';

  return (
    <>
      <Seo title={post.title} description={post.excerpt || stripHtml(post.content)} image={post.image} type="article" seo={post.seo || {}} jsonLd={jsonLd} breadcrumbs={[{ label: 'Home', url: '/' }, { label: title, url: `/blogs/${blogSlug}` }, { label: post.title }]} keywords={post.tags}>
        <meta property="article:published_time" content={post.publishedAt} />
        {post.category && <meta property="article:section" content={post.category} />}
      </Seo>
      <Breadcrumb items={[{ label: title, url: `/blogs/${blogSlug}` }, { label: post.title }]} />
      <article className="sf-custom sf__article">
        <div className="container-fluid section-my" style={{ maxWidth: 1000 }}>
          <header className="section__header text-center">
            {post.category && <p className="section__subheading">{post.category}</p>}
            <h1 className="section__heading">{post.title}</h1>
            <div className="section__description text-color-secondary">{post.author || settings.general?.siteName} - {formatDate(post.publishedAt, { month: 'long', day: 'numeric', year: 'numeric' })}</div>
          </header>
          {post.image && (
            <div className="sf-custom__block sf-custom__block--image my-8"><div className="sf__hover-scale-up overflow-hidden rounded-[5px]"><img src={mediaUrl(post.image)} alt={post.title} className="w-full" /></div></div>
          )}
          <div className="prose rte mt-4 text-color-subtext eg-article-content" dangerouslySetInnerHTML={{ __html: post.content }} />
          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-8">
              {post.tags.map((t) => <Link key={t} to={`/blogs/${blogSlug}?category=${encodeURIComponent(t)}`} className="eg-tag">{t}</Link>)}
            </div>
          )}
          <div className="flex justify-between border-t mt-10 pt-6 gap-4">
            <div>{prev && <Link to={`/blogs/${blogSlug}/${prev.slug}`} className="hover:underline"><span className="text-xs text-color-secondary block">← Previous</span>{prev.title}</Link>}</div>
            <div className="text-right">{next && <Link to={`/blogs/${blogSlug}/${next.slug}`} className="hover:underline"><span className="text-xs text-color-secondary block">Next →</span>{next.title}</Link>}</div>
          </div>
        </div>
      </article>
      {related?.length > 0 && (
        <section className="sf__blog-posts bg-gray-50">
          <div className="container-fluid py-16">
            <div className="section__header text-center"><h2 className="section__heading">You may also like</h2></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">{related.map((p) => <BlogCard key={p._id} post={p} blogSlug={blogSlug} />)}</div>
          </div>
        </section>
      )}
    </>
  );
}
